import { useState } from 'react';
import { SimpleRegistration, SimpleRegistrationErrors } from '../types';
import { validateSimpleRegistration } from '../utils/simpleRegistrationValidation';
import { useAuth } from './useAuth';

interface UseSimpleRegistrationReturn {
  formData: Partial<SimpleRegistration>;
  errors: SimpleRegistrationErrors;
  isSubmitting: boolean;
  isSuccess: boolean;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  resetForm: () => void;
  resetSuccess: () => void;
}

const useSimpleRegistration = (): UseSimpleRegistrationReturn => {
  const { register } = useAuth();
  const [formData, setFormData] = useState<Partial<SimpleRegistration>>({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: ''
  });
  const [errors, setErrors] = useState<SimpleRegistrationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name as keyof SimpleRegistrationErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationErrors = validateSimpleRegistration(formData);
    setErrors(validationErrors);
    
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Convertir SimpleRegistration a CompanyRegistration para el hook useAuth
      const registrationData = {
        email: formData.email!,
        password: formData.password!,
        firstName: formData.fullName!.split(' ')[0] || 'Usuario',
        lastName: formData.fullName!.split(' ').slice(1).join(' ') || 'Empresa',
        // Campos requeridos por CompanyRegistration (valores temporales)
        razonSocial: formData.fullName!,
        nombreFantasia: formData.fullName!,
        direccion: '',
        pais: 'ARGENTINA',
        provincia: '',
        localidad: '',
        codigoPostal: '',
        ddn: '',
        telefono1: '',
        tipoActividad: '',
        descripcion: '',
        rubros: '',
        comoNosConocio: '',
        contacto: '',
        usuario: formData.email!,
        confirmPassword: formData.confirmPassword!,
        terminos: true
      };

      const result = await register(registrationData);
      
      if (result.success) {
        setIsSuccess(true);
        resetForm();
      } else {
        setErrors({ 
          general: result.message || 'Error al registrar la empresa' 
        });
      }
    } catch (error) {
      console.error('Error al enviar el formulario:', error);
      setErrors({ 
        general: 'Error inesperado. Por favor, inténtalo de nuevo.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      fullName: ''
    });
    setErrors({});
  };

  const resetSuccess = () => {
    setIsSuccess(false);
  };

  return {
    formData,
    errors,
    isSubmitting,
    isSuccess,
    handleInputChange,
    handleSubmit,
    resetForm,
    resetSuccess
  };
};

export default useSimpleRegistration;
